import { message } from 'antd';

export const createUploadHandlers = (caseDetails, fetchCaseDetails, apiClasses) => {
    if (!caseDetails || !apiClasses) return null;
    
    return {
        handleFileUpload: async ({ file, onSuccess, onError, onProgress }) => {
            try {
                // Get the upload URL that was stored in beforeUpload
                const uploadUrl = file.uploadUrl;
                
                if (!uploadUrl) {
                    onError(new Error('Upload URL not available'));
                    return;
                }
                
                // Convert file to ArrayBuffer (same as original)
                const getArrayBufferFromFile = async () => new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.readAsArrayBuffer(file);
                });

                const data = await getArrayBufferFromFile();

                // Upload file to S3 using fetch (avoiding axios dependency)
                const response = await fetch(uploadUrl, {
                    method: 'PUT',
                    body: data,
                    headers: {
                        'Content-Type': file.type || 'application/octet-stream'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Upload failed with status ${response.status}`);
                }

                onSuccess({ success: true });
            } catch (err) {
                onError(err);
            }
        },
        
        handleUploadChange: async (info) => {
            if (info.file.status === 'done') {
                try {
                    const caseId = caseDetails?.caseId || caseDetails?._id;
                    const uploadUrl = info.file.uploadUrl;
                    
                    if (!uploadUrl) {
                        message.error('Upload URL not available');
                        return;
                    }
                    
                    // Construct document URL the same way as the original
                    const url = new URL(uploadUrl);
                    const documentUrl = `${url.origin}${url.pathname}`;
                    
                    // Add document to case - try different API methods
                    let success = false;
                    
                    if (apiClasses?.CaseRegisterApi?.addAdditionalDocument) {
                        await apiClasses.CaseRegisterApi.addAdditionalDocument(caseId, documentUrl);
                        success = true;
                    } else if (apiClasses?.CasesApi?.addAdditionalDocument) {
                        await apiClasses.CasesApi.addAdditionalDocument(caseId, documentUrl);
                        success = true;
                    } else if (apiClasses?.PhysicianAPI?.addAdditionalDocumentUrlToCase) {
                        await apiClasses.PhysicianAPI.addAdditionalDocumentUrlToCase(caseId, documentUrl);
                        success = true;
                    }
                    
                    if (success && fetchCaseDetails) {
                        await fetchCaseDetails();
                    }
                } catch (error) {
                    console.error('Error adding document to case:', error);
                    message.error('Error saving document to case');
                }
            } else if (info.file.status === 'error') {
                message.error('Error uploading document');
            }
        },
        
        handleBeforeUpload: async (file) => {
            try {
                const caseId = caseDetails?.caseId || caseDetails?._id;

                // Get signed upload URL - try different API methods
                let response = null;

                if (apiClasses?.CaseRegisterApi?.getSignedUploadUrl) {
                    response = await apiClasses.CaseRegisterApi.getSignedUploadUrl(caseId, file.name);
                } else if (apiClasses?.CasesApi?.getSignedUploadUrl) {
                    response = await apiClasses.CasesApi.getSignedUploadUrl(caseId, file.name);
                } else if (apiClasses?.PhysicianAPI?.getSignedUploadUrl) {
                    response = await apiClasses.PhysicianAPI.getSignedUploadUrl(caseId, file.name);
                }

                if (response && response.data?.signedUploadUrl) {
                    // Store the upload URL on the file object for use in handleFileUpload
                    file.uploadUrl = response.data.signedUploadUrl;
                    return true;
                } else {
                    message.error('Upload functionality not available');
                    return false;
                }
            } catch (error) {
                console.error('Error getting upload URL:', error);
                message.error('Error preparing upload');
                return false;
            }
        },

        handleDeleteDocument: async (documentName) => {
            try {
                const caseId = caseDetails?.caseId || caseDetails?._id;
                const api = [apiClasses?.CasesApi, apiClasses?.PhysicianAPI]
                    .find(a => a?.deleteAdditionalDocument);

                if (!api) {
                    message.error('Delete functionality not available');
                    return;
                }

                await api.deleteAdditionalDocument(caseId, documentName);
                message.success('Document deleted');
                if (fetchCaseDetails) await fetchCaseDetails();
            } catch (error) {
                console.error('Error deleting document:', error);
                message.error('Error deleting document');
            }
        }
    };
};
